import {
  ActionIcon,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  Pagination,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { safeApiCall } from "api/common.service";
import { logError } from "api/diagnostic.service";
import { getSupplierCodes } from "api/supplier.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
  MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
} from "common/constants";
import { DEFAULT_PAGE_SIZE } from "components/layouts/consts";
import React, { useEffect, useRef, useState } from "react";
import {
  PaginatedSupplierProductCodes,
  Supplier,
  SupplierProductCode,
} from "types/model.types";

import { IconEdit, IconPlus } from "@tabler/icons-react";
import AdminSupplierProductCodeEntryPage from "./AdminSupplierProductCodeEntryPage";

interface SupplierEditFormProps {
  supplier?: Supplier;
  onSaveComplete: (values: Supplier) => void;
  onCancel: () => void;
}

const AdminSupplierProductCodePage: React.FC<SupplierEditFormProps> = ({
  supplier,
  onSaveComplete,
  onCancel,
}) => {
  const apiFetchRef = useRef(false);
  const [reloadTrigger, setReloadTrigger] = useState(new Date());
  const [loading, setLoading] = useState<boolean>(true);
  const [activePage, setActivePage] = useState(1);
  const [supplierCodeModelOpen, setSupplierCodeModelOpen] = useState(false);

  const [selectedCode, setSelectedCode] = useState<
    SupplierProductCode | undefined
  >(undefined);
  const [supplierCodes, setSupplierCodes] =
    useState<PaginatedSupplierProductCodes>({
      data: [],
      total: 0,
      pageNumber: 1,
      pageSize: DEFAULT_PAGE_SIZE,
    });

  useEffect(() => {
    if (!supplier) return;
    if (apiFetchRef.current) return; // if already fetched, do not refetch
    apiFetchRef.current = true;
    setLoading(true);

    const fetchData = async () => {
      try {
        const response = await safeApiCall(
          getSupplierCodes(Number(supplier?.id), activePage, DEFAULT_PAGE_SIZE),
        );
        if (response) {
          setSupplierCodes(response);
        }
      } catch (err) {
        logError(err);
        notifications.show({
          message: MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
          position: MantineNotificationPosition.TopCenter,
          color: MantineNotificationColor.Error,
        });
      } finally {
        apiFetchRef.current = false;
        setLoading(false);
      }
    };
    fetchData();
  }, [supplier, activePage, reloadTrigger]);

  const handleSubmit = async () => {
    // const result = form.validate();
    // if (result.hasErrors) {
    //   notifications.show({
    //     title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
    //     message: MSG_DEFAULT_FORM_INVALID,
    //     position: MantineNotificationPosition.TopCenter,
    //     color: MantineNotificationColor.Error,
    //   });
    //   return;
    // }
    // const payload: Supplier = form.getValues();
    // try {
    //   const result = await safeApiCall(submitSupplier(payload));
    //   notifications.show({
    //     title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
    //     message: MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
    //     position: MantineNotificationPosition.TopCenter,
    //     color: MantineNotificationColor.Success,
    //   });
    //   onSaveComplete(result);
    // } catch {
    //   notifications.show({
    //     title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
    //     message: MSG_DEFAULT_FORM_SUBMIT_ERR,
    //     position: MantineNotificationPosition.TopCenter,
    //     color: MantineNotificationColor.Error,
    //   });
    // }
  };
  const rows = supplierCodes.data.map((entry) => {
    return (
      <Table.Tr key={entry.id}>
        <Table.Td className="action-cell">
          <Group gap="xs" wrap="nowrap">
            <Tooltip label="Edit">
              <ActionIcon
                variant="subtle"
                color="blue"
                onClick={() => {
                  setSelectedCode(entry);
                  setSupplierCodeModelOpen(true);
                }}
              >
                <IconEdit size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Table.Td>
        <Table.Td className="number">{entry.productId}</Table.Td>
        <Table.Td>{entry.alternateCode}</Table.Td>
        <Table.Td>{entry.descriptions}</Table.Td>
      </Table.Tr>
    );
  });
  const handleSupplierCodeSaved = (saved: SupplierProductCode) => {
    setSupplierCodes((prev) => {
      const exists = prev.data.some((s) => s.id === saved.id);

      let newData;

      if (exists) {
        // UPDATE existing supplier
        newData = prev.data.map((s) => (s.id === saved.id ? saved : s));
      } else {
        // ADD new supplier
        newData = [saved, ...prev.data];
      }

      return {
        ...prev,
        data: newData,
        total: exists ? prev.total : prev.total + 1,
      };
    });

    // Close modal, clear selection, etc.
    setSelectedCode(undefined);
    setSupplierCodeModelOpen(false);
  };
  return (
    <Stack>
      <Title order={2}>Supplier: {supplier?.supplierName}</Title>
      {loading ? (
        <Center>
          <Loader />
        </Center>
      ) : (
        <>
          <Group justify="space-between" wrap="nowrap" mb="md">
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                setSelectedCode({
                  id: 0,
                  productId: 0,
                  alternateCode: "",
                  descriptions: "",
                  supplierId: Number(supplier?.id ?? 0),
                });
                setSupplierCodeModelOpen(true);
              }}
            >
              Add Product Code
            </Button>
            <Group justify="flex-end" wrap="nowrap" mb="md">
              <Text size="sm" c="dimmed">
                Showing 30 items per page
              </Text>
              <Pagination
                total={Math.ceil(supplierCodes.total / DEFAULT_PAGE_SIZE)}
                value={activePage}
                onChange={setActivePage}
                siblings={1}
                boundaries={1}
              />
            </Group>
          </Group>
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ whiteSpace: "nowrap" }}>Actions</Table.Th>
                <Table.Th className="number">Product #</Table.Th>
                <Table.Th>Alternate Code</Table.Th>
                <Table.Th style={{ minWidth: 600 }}>Description</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
          <Group justify="flex-end" wrap="nowrap" mb="md">
            <Pagination
              total={Math.ceil(supplierCodes.total / DEFAULT_PAGE_SIZE)}
              value={activePage}
              onChange={setActivePage}
              siblings={1}
              boundaries={1}
            />
          </Group>
        </>
      )}
      {/* main supplier modal */}
      <Modal
        opened={supplierCodeModelOpen}
        onClose={() => setSupplierCodeModelOpen(false)}
        title="Add | Edit Supplier Code"
        size="xl"
        closeOnEscape={false}
        centered
        withinPortal={true} // default, but explicit here
        zIndex={2001} // ensure it's above layout elements
      >
        <AdminSupplierProductCodeEntryPage
          initial={selectedCode}
          onSaveComplete={(saved: SupplierProductCode) => {
            handleSupplierCodeSaved(saved);
          }}
          onCancel={() => {
            setSelectedCode(undefined);
            setSupplierCodeModelOpen(false);
          }}
        />
      </Modal>
    </Stack>
  );
};

export default AdminSupplierProductCodePage;
